import { CurrentConsumerProjectProvider } from "@/lib/context/current-consumer-project-context";
import { zrpc, type GetFeatureFlagsOutput } from "@/lib/zrpc";
import { EmbeddedChatbot } from "block-system/blocks/Chatbot/Block/EmbeddedChatbot";
import { EmbeddedChatbotFallback } from "block-system/blocks/Chatbot/Block/EmbeddedChatbotFallback";
import { getFrameAncestorsForChatbot } from "block-system/blocks/Chatbot/getFrameAncestorsForChatbot";
import { map as mapChatbotBlock } from "block-system/blocks/Chatbot/operations";
import { ChatbotBlock } from "block-system/blocks/Chatbot/types";
import PublishedProvider from "components/PublishedProvider";
import { getCSPHeader } from "get-csp-header";
import { ConsumerProvider } from "lib/context/consumer-provider";
import { EmbedProvider } from "lib/context/embed-context";
import { PageContextProvider } from "lib/context/page-context";
import { setProjectSession } from "lib/project-sessions/project-session-storage";
import type { InterfacesTheme } from "lib/theme/ThemeProvider";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";
import { prisma } from "server/data/prisma";
import { withProjectSessionSSR } from "server/hooks/withProjectSession";
import { PageContentInclude } from "server/models/blocks";
import { getProjectFromSlugOrCustomDomain } from "server/models/projects";
import { getThemeByProjectId } from "server/models/theme";
import logger from "server/observability/logger";
import {
  consumerProjectSchema,
  ProjectAppearance,
  ProjectAppearanceSchema,
  projectForPublishedPageSchema,
} from "server/schemas/projects";
import { getFeatureFlags } from "server/services/split/split";
import { z } from "zod";
import { withErrorCatchingGetServerSideProps } from "@/server/hooks/withErrorCatching";

const ParsedUrlQuerySchema = z
  .object({
    projectSlug: z.string(),
    chatbotId: z.string().cuid(),
    isPopup: z.string().default("false"),
    pageId: z.string().cuid(),
  })
  .catchall(z.string());

type ProjectForPublishedPage = z.infer<typeof projectForPublishedPageSchema>;

type Props = {
  fallback?: ReactNode;
  projectId: string;
  pageId: string;
  block: ChatbotBlock;
  blockId: string;
  projectAppearance?: ProjectAppearance;
  isPopup?: boolean;
  project: ProjectForPublishedPage;
  chatbotId: string;
  splitCheck: GetFeatureFlagsOutput;
  builderFlags: GetFeatureFlagsOutput;
  sessionId: string;
  configParams: Record<string, unknown>;
  theme: InterfacesTheme;
};

function EmbeddedChatbotWithProviders(props: Props) {
  const { projectId, pageId, isPopup, project } = props;

  if (!projectId) {
    throw new Error("Interface not found handle by Suspense");
  }

  const pageBackground = isPopup ? "transparent" : props.theme.background;

  return (
    <>
      <style global jsx>
        {`
          body {
            background-color: ${pageBackground} !important;
          }
        `}
      </style>
      <CurrentConsumerProjectProvider
        project={consumerProjectSchema.parse(project)}
      >
        <ConsumerProvider>
          <PageContextProvider
            projectId={projectId}
            pageId={pageId}
            isEditing={false}
            isEmbedded={true}
          >
            <EmbeddedChatbot {...props} />
          </PageContextProvider>
        </ConsumerProvider>
      </CurrentConsumerProjectProvider>
    </>
  );
}

export default function EmbeddedChatbotWithProvider(props: Props) {
  const queryClient = useQueryClient();

  const embed = useMemo(
    () => ({
      isEmbed: true,
      isPopup: props.isPopup,
      noBackground: props.isPopup,
    }),
    [props]
  );

  setProjectSession({ sessionId: props.sessionId, pageId: props.pageId });

  const getFeatureFlagsQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/internal/feature-flags",
    { params: { query: { project_id: props.project.id } } }
  );
  const cachedFeatureFlags = queryClient.getQueryData(
    getFeatureFlagsQueryOptions.queryKey
  );
  if (!cachedFeatureFlags) {
    queryClient.setQueryData(
      getFeatureFlagsQueryOptions.queryKey,
      props.splitCheck
    );
  }

  return (
    <PublishedProvider
      project={props.project}
      fallback={<EmbeddedChatbotFallback />}
      interfacesTheme={props.theme}
      builderFlags={props.builderFlags}
    >
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <EmbedProvider embed={embed}>
        <EmbeddedChatbotWithProviders {...props} />
      </EmbedProvider>
    </PublishedProvider>
  );
}

export const getServerSideProps: GetServerSideProps<Props> =
  withErrorCatchingGetServerSideProps(
    withProjectSessionSSR<Props>(async ({ query, req, res }) => {
      const parsedQueryResult = ParsedUrlQuerySchema.safeParse(query);
      if (!parsedQueryResult.success) return { notFound: true };
      const params = parsedQueryResult.data;
      const configParams = {
        ...params,
        // we also need to nest under `params` for proper placeholder replacement; ex: {{params.name|default:'Brian'}}
        params,
      };

      // TODO: (INTRFCS-3021) abstract and unit test this logic
      const dbBlock = await prisma.block.findUnique({
        where: { id: params.chatbotId },
        include: {
          ...PageContentInclude.blocks.include,
          page: { include: { project: { include: { creator: true } } } },
        },
      });

      if (!dbBlock) return { notFound: true };
      const chatbotBlock = mapChatbotBlock(dbBlock);

      const creator = dbBlock.page.project.creator;
      const chatbotId = chatbotBlock.config.chatbotId;
      if (!chatbotId) {
        logger().error(
          { blockId: params.chatbotId },
          "No chatbot connected to this chatbot block"
        );
        return { notFound: true };
      }

      // Save the project session data
      req.projectSession.data = {
        ...req.projectSession.data,
        enableChatbot: true,
        projectId: dbBlock.page.project.id,
      };

      // Set frame-ancestors Content-Security-Policy header
      const frameAncestors = getFrameAncestorsForChatbot(
        dbBlock.page.project.embedHosts,
        dbBlock.page.project.restrictEmbedDomains
      );
      res.setHeader("Content-Security-Policy", getCSPHeader(frameAncestors));

      res.setHeader("X-Robots-Tag", "noindex, nofollow");

      await req.projectSession.save();

      const parsedAppearance = ProjectAppearanceSchema.safeParse(
        dbBlock.page.project.appearance
      );

      const [theme, builderFlags, splitCheck, project] =
        await Promise.allSettled([
          getThemeByProjectId(dbBlock.page.project.id),
          getFeatureFlags({
            zapierUser: creator,
            project: undefined,
          }),
          getFeatureFlags({
            zapierUser: undefined,
            project: dbBlock.page.project,
          }),
          getProjectFromSlugOrCustomDomain(params.projectSlug).then(
            (projectValue) => {
              if (!projectValue) {
                throw new Error("Project not found");
              }

              /**
               * It's important that we parse the project into the fields explicitly made public in
               * order to prevent any sensitive data contained in the project from being sent over the
               * wire.
               */
              return projectForPublishedPageSchema.parse(projectValue);
            }
          ),
        ]);

      if (
        theme.status === "rejected" ||
        builderFlags.status === "rejected" ||
        splitCheck.status === "rejected" ||
        project.status === "rejected"
      ) {
        return { notFound: true };
      }

      return {
        props: {
          projectId: dbBlock.page.project.id,
          chatbotId: chatbotId,
          pageId: dbBlock.page.id,
          block: chatbotBlock,
          blockId: dbBlock.id,
          projectAppearance: parsedAppearance.success
            ? parsedAppearance.data
            : undefined,
          isPopup: query.isPopup === "true",
          project: project.value,
          configParams,
          splitCheck: splitCheck.value,
          builderFlags: builderFlags.value,
          sessionId: req.projectSession.id,
          theme: theme.value,
        },
      };
    })
  );
